import { Box, Group, Stack, Text } from '@mantine/core'

interface WeekData {
  week: string
  success: number
  failed: number
}

interface DeployFrequencyChartProps {
  data: WeekData[]
}

const BAR_MIN_HEIGHT = 4
const BAR_MAX_HEIGHT = 120

export function getISOWeek(date: Date): { year: number; week: number } {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7))
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
  const week = Math.ceil(((d.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7)
  return { year: d.getUTCFullYear(), week }
}

function isCurrentWeek(week: string): boolean {
  const { year, week: w } = getISOWeek(new Date())
  return week === `${year}-W${String(w).padStart(2, '0')}`
}

export function DeployFrequencyChart({ data }: DeployFrequencyChartProps) {
  if (data.length === 0) {
    return (
      <Box py="xl">
        <Text ta="center" c="dimmed" size="sm">Nenhum deploy registrado nos últimos 30 dias</Text>
      </Box>
    )
  }

  const maxTotal = Math.max(...data.map(d => d.success + d.failed), 1)

  const barHeight = (value: number) =>
    Math.max(BAR_MIN_HEIGHT, Math.round((value / maxTotal) * BAR_MAX_HEIGHT))

  return (
    <Stack gap="sm">
      <Group gap="xs" align="flex-end" justify="center" style={{ minHeight: BAR_MAX_HEIGHT + 24 }}>
        {data.map((d) => {
          const total = d.success + d.failed
          const current = isCurrentWeek(d.week)
          const successColor = current ? 'var(--mantine-color-green-6)' : 'var(--mantine-color-blue-6)'
          const failedColor = 'var(--mantine-color-red-6)'

          return (
            <Stack key={d.week} gap={0} align="center" style={{ flex: 1, maxWidth: 60 }}>
              <Text size="xs" c="dimmed" mb={4}>{total}</Text>
              <Box style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {d.failed > 0 && (
                  <Box
                    style={{
                      width: 24,
                      height: barHeight(d.failed),
                      backgroundColor: failedColor,
                      borderRadius: '3px 3px 0 0',
                    }}
                  />
                )}
                {d.success > 0 && (
                  <Box
                    style={{
                      width: 24,
                      height: barHeight(d.success),
                      backgroundColor: successColor,
                      borderRadius: d.failed > 0 ? '0 0 3px 3px' : 3,
                    }}
                  />
                )}
              </Box>
              <Text size="xs" c="dimmed" mt={4}>{d.week.split('-W')[1] ? `S${d.week.split('-W')[1]}` : d.week}</Text>
            </Stack>
          )
        })}
      </Group>

      <Group gap="md" justify="center">
        <Group gap={4}>
          <Box style={{ width: 12, height: 12, backgroundColor: 'var(--mantine-color-blue-6)', borderRadius: 2 }} />
          <Text size="xs" c="dimmed">Success</Text>
        </Group>
        <Group gap={4}>
          <Box style={{ width: 12, height: 12, backgroundColor: 'var(--mantine-color-red-6)', borderRadius: 2 }} />
          <Text size="xs" c="dimmed">Failed</Text>
        </Group>
        <Group gap={4}>
          <Box style={{ width: 12, height: 12, backgroundColor: 'var(--mantine-color-green-6)', borderRadius: 2 }} />
          <Text size="xs" c="dimmed">Semana atual</Text>
        </Group>
      </Group>
    </Stack>
  )
}
